import { useState, useRef, useCallback } from "react";

const INITIAL_STATE = {
	status: "idle",
	total: 0,
	completed: 0,
	succeeded: 0,
	failed: 0,
	errors: [],
	percentage: 0,
};

export default function useBatchOperation({ batchSize = 5 } = {}) {
	const [state, setState] = useState(INITIAL_STATE);
	const cancelledRef = useRef(false);

	const execute = useCallback(async (tasks) => {
		cancelledRef.current = false;
		const total = tasks.length;

		setState({
			status: "running",
			total,
			completed: 0,
			succeeded: 0,
			failed: 0,
			errors: [],
			percentage: 0,
		});

		let succeeded = 0;
		let failed = 0;
		const errors = [];

		for (let i = 0; i < total; i += batchSize) {
			if (cancelledRef.current) {
				setState((prev) => ({ ...prev, status: "cancelled" }));
				return { succeeded, failed, errors, cancelled: true };
			}

			const batch = tasks.slice(i, i + batchSize);
			const results = await Promise.allSettled(batch.map((t) => t.run()));

			for (const result of results) {
				if (result.status === "fulfilled") {
					succeeded++;
				} else {
					failed++;
					errors.push(result.reason);
				}
			}

			const completed = Math.min(i + batchSize, total);
			setState({
				status: "running",
				total,
				completed,
				succeeded,
				failed,
				errors,
				percentage: Math.round((completed / total) * 100),
			});
		}

		setState((prev) => ({ ...prev, status: "completed" }));
		return { succeeded, failed, errors, cancelled: false };
	}, [batchSize]);

	const cancel = useCallback(() => {
		cancelledRef.current = true;
	}, []);

	return { execute, cancel, state };
}
